<script setup lang="ts">
import type { WatchStopHandle } from 'vue';
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';

import type { ILoaderProps, ILoaderSlots, LoaderSpinnerSize } from './types';

const props = withDefaults(defineProps<ILoaderProps>(), {
  displaySpinner: true,
  spinnerSize: 'medium',
  mode: 'structural-immediate',
  minHeight: '100px',
  minWidth: '200px',
  minDurationMS: 300,
  strokeWidth: '2px',
});

defineSlots<ILoaderSlots>();

const isLoading = ref<boolean>(false);
let readyWatcher: WatchStopHandle;
const ready = ref(false);
let timeout: ReturnType<typeof setTimeout>;

const SPINNER_SIZES: { [key in LoaderSpinnerSize]: string } = {
  small: '2rem',
  medium: '3rem',
  large: '4rem',
};

const spinnerStyle = computed(() => {
  const size = SPINNER_SIZES[props.spinnerSize];
  return `height: ${size}; width: ${size};`;
});

const spinnerWrapperClassFull = computed(() => [props.spinnerWrapperClass, { loading: isLoading.value }]);

onMounted(() => {
  isLoading.value = props.loading;
});

watch(
  () => props.loading,
  (newValue) => {
    if (!newValue) {
      readyWatcher = watch(
        ready,
        (value) => {
          if (value) {
            isLoading.value = false;
            if (readyWatcher) {
              readyWatcher();
            }
          }
        },
        { immediate: true },
      );
    }

    if (newValue) {
      if (!isLoading.value) {
        ready.value = false;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          ready.value = true;
        }, props.minDurationMS);
      }

      if (readyWatcher) {
        readyWatcher();
      }
      isLoading.value = true;
    }
  },
  { immediate: true },
);
</script>

<template>
  <template v-data-qa-auto-id="'VLoader'" v-if="mode === 'structural-immediate'">
    <Transition
      name="fade"
      mode="out-in"
    >
      <slot
        v-if="!isLoading"
      />
      <slot
        v-else
        name="loader"
      >
        <div
          v-if="displaySpinner"
          class="flex items-center justify-center"
          :class="spinnerWrapperClassFull"
          :style="{ minHeight, minWidth }"
        >
          <PProgressSpinner
            :style="spinnerStyle"
            :stroke-width="strokeWidth"
          />
        </div>
        <div
          v-else
          class="loader-placeholder"
          :class="spinnerWrapperClassFull"
          :style="{ minHeight, minWidth }"
        />
      </slot>
    </Transition>
  </template>

  <template v-else-if="mode === 'structural'">
    <div
      class="loader-wrapper"
      :style="{ minHeight, minWidth }"
    >
      <Transition name="fade">
        <slot v-if="!isLoading" />
        <div
          v-else
          :class="spinnerWrapperClassFull"
          class="loader-overlay"
        >
          <slot name="loader">
            <PProgressSpinner
              v-if="displaySpinner"
              :style="spinnerStyle"
              :stroke-width="strokeWidth"
            />
          </slot>
        </div>
      </Transition>
    </div>
  </template>

  <template v-else-if="mode === 'overlay'">
    <div
      class="loader-wrapper-overlay"
      :class="{ loading: isLoading }"
    >
      <slot />
      <div
        :class="spinnerWrapperClassFull"
        class="loader-overlay"
      >
        <slot name="loader">
          <PProgressSpinner
            v-if="displaySpinner"
            :style="spinnerStyle"
          />
        </slot>
      </div>
    </div>
  </template>
</template>

<style scoped lang="scss">
$duration: 300ms;
$durationOverlayIn: 150ms;

.fade-enter-active,
.fade-leave-active {
  transition: opacity $duration ease-in;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.loader-wrapper,
.loader-wrapper-overlay {
  position: relative;

  .loader-overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
  }
}

:deep(.loader-wrapper-overlay) {
  & > * {
    opacity: 1;
    transition: opacity $duration ease-in;
  }

  &.loading {
    & > * {
      opacity: 0;
    }
  }
}

.loader-wrapper-overlay {
  .loader-overlay {
    pointer-events: none;
    user-select: none;
    opacity: 0;
    transition: opacity $duration ease-in;

    &.loading {
      opacity: 1;
      transition: opacity $durationOverlayIn ease-in;
    }
  }
}
</style>
